/**
 * CallButton (Phase 4 / Chunk 2)
 *
 * Click-to-call trigger. Disabled when the contact has no phone, voice isn't
 * available (env not set / not entitled), or a call is already in progress. The
 * actual dialed number is resolved server-side (test override → your cell), so
 * this only passes the intended contact number + id.
 */
import { Phone } from 'lucide-react';
import { useSoftphone } from '../contexts/SoftphoneContext';

interface Props {
  to: string | null;
  contactId?: string;
  name?: string;
  className?: string;
}

export default function CallButton({ to, contactId, name, className }: Props) {
  const { startCall, unavailable, callStatus } = useSoftphone();
  const inCall = callStatus !== 'idle';
  const disabled = !to || unavailable || inCall;
  const title = !to
    ? 'No phone number on file'
    : unavailable
    ? 'Calling is not available'
    : inCall
    ? 'A call is already in progress'
    : 'Call this contact';

  return (
    <button
      onClick={() => to && startCall({ to, contactId, name })}
      disabled={disabled}
      title={title}
      className={
        className ||
        'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0'
      }
    >
      <Phone className="h-3.5 w-3.5" /> Call
    </button>
  );
}
